<template>
  <b-modal
    v-model="visible"
    modal-class="lz-activity-modal lizard-resource-modal lizard-sections-modal"
    content-class="lz-modal-content"
    size="md"
    centered
    scrollable
    @hidden="Closed">
    <template #modal-header="{ close }">
      <div>
        <h5 class="lizard-rm-title">Manage Sections</h5>
        <p class="lizard-rm-subtitle">
          Rename, reorder, or show/hide sections in “{{ tabLabel }}”. Boxes inside are managed on the tab itself.
        </p>
      </div>
      <b-button-close @click="close()"></b-button-close>
    </template>

    <ul class="lizard-ms-list">
      <li
        class="lizard-ms-row"
        :class="{
          'lizard-ms-row-dragging': draggingIndex === index,
          'lizard-ms-row-drag-over': dragOverIndex === index && draggingIndex !== index
        }"
        v-for="(row, index) in rows"
        :key="row.uid"
        @dragover="OnDragOver(index, $event)"
        @dragleave="OnDragLeave(index)"
        @drop="OnDrop(index, $event)">
        <span
          class="lizard-ms-handle"
          draggable="true"
          title="Drag to reorder"
          @dragstart="OnDragStart(index, $event)"
          @dragend="OnDragEnd">
          <b-icon-grip-vertical scale="0.9"></b-icon-grip-vertical>
        </span>
        <input
          type="text"
          class="lizard-ms-input"
          v-model="row.title"
          @keyup.enter="SaveTitle(row)"
          @blur="SaveTitle(row)" />
        <b-spinner v-if="row.savingTitle" small class="lizard-ms-spinner"></b-spinner>
        <button
          type="button"
          class="lizard-rm-toggle lizard-ms-toggle"
          :class="{ on: row.show }"
          :disabled="row.savingShow"
          @click="ToggleShow(row)">
          <span class="lizard-rm-toggle-track">
            <span class="lizard-rm-toggle-knob"></span>
          </span>
          <span class="lizard-rm-toggle-label">{{ row.show ? 'Visible' : 'Hidden' }}</span>
        </button>
      </li>
      <li v-if="!rows.length" class="lizard-ms-empty">No sections yet.</li>
    </ul>

    <div class="lizard-ms-add">
      <input
        type="text"
        class="lizard-ms-input lizard-ms-add-input"
        v-model="newSectionTitle"
        placeholder="New section name"
        @keyup.enter="AddSection" />
      <b-button
        size="sm"
        class="lz-pill-btn"
        :disabled="addingSection || !newSectionTitle.trim().length"
        @click="AddSection">
        <b-spinner v-if="addingSection" small></b-spinner>
        <template v-else><b-icon-plus></b-icon-plus>Add Section</template>
      </b-button>
    </div>
    <p class="lizard-rm-hint lizard-ms-add-hint">New sections start hidden until you make them visible.</p>

    <template #modal-footer>
      <b-button
        size="sm"
        variant="primary"
        class="lz-close-btn lizard-rm-save"
        @click="visible = false">
        Done
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Services from '../../services/main.service.vue'

export default {
  name: 'HomeManageSectionsModal',
  props: {
    tab: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      visible: false,
      rows: [],
      changed: false,
      newSectionTitle: '',
      addingSection: false,
      draggingIndex: null,
      dragOverIndex: null
    }
  },
  computed: {
    tabLabel() {
      const labels = {
        'useful-links': 'Useful Links',
        training: 'Training Modules',
        forms: 'Documents and Forms'
      }
      return labels[this.tab] ?? this.tab
    }
  },
  methods: {
    Open(sections = []) {
      this.rows = sections.map((section) => ({
        uid: section.uid,
        title: section.title,
        originalTitle: section.title,
        show: section.show !== false,
        savingTitle: false,
        savingShow: false
      }))
      this.newSectionTitle = ''
      this.changed = false
      this.visible = true
    },
    Closed() {
      if (this.changed) {
        this.$emit('changed')
      }
    },
    async SaveTitle(row) {
      const title = row.title.trim()
      if (!title.length) {
        row.title = row.originalTitle
        return
      }
      if (title === row.originalTitle) {
        return
      }
      try {
        row.savingTitle = true
        const { status, data } = await Services.Home.UpdateHomeResource(row.uid, { title })
        if (status === 200 && data?.success !== false) {
          row.originalTitle = title
          row.title = title
          this.changed = true
        } else {
          row.title = row.originalTitle
          this.ShowError(data)
        }
      } catch (error) {
        row.title = row.originalTitle
        this.ShowError(error?.response?.data ?? error)
      } finally {
        row.savingTitle = false
      }
    },
    async ToggleShow(row) {
      try {
        row.savingShow = true
        const nextShow = !row.show
        const { status, data } = await Services.Home.UpdateHomeResource(row.uid, { show: nextShow })
        if (status === 200 && data?.success !== false) {
          row.show = nextShow
          this.changed = true
        } else {
          this.ShowError(data)
        }
      } catch (error) {
        this.ShowError(error?.response?.data ?? error)
      } finally {
        row.savingShow = false
      }
    },
    async AddSection() {
      const title = this.newSectionTitle.trim()
      if (!title.length || this.addingSection) {
        return
      }
      try {
        this.addingSection = true
        const { status, data } = await Services.Home.CreateHomeResource({
          tab: this.tab,
          title,
          isSection: true,
          sortOrder: this.rows.length
        })
        if (status === 200 && data?.success !== false) {
          const created = data.content
          this.rows.push({
            uid: created.uid,
            title: created.title,
            originalTitle: created.title,
            show: created.show !== false,
            savingTitle: false,
            savingShow: false
          })
          this.newSectionTitle = ''
          this.changed = true
        } else {
          this.ShowError(data)
        }
      } catch (error) {
        this.ShowError(error?.response?.data ?? error)
      } finally {
        this.addingSection = false
      }
    },
    OnDragStart(index, event) {
      this.draggingIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    OnDragOver(index, event) {
      event.preventDefault()
      this.dragOverIndex = index
    },
    OnDragLeave(index) {
      if (this.dragOverIndex === index) {
        this.dragOverIndex = null
      }
    },
    async OnDrop(index, event) {
      event.preventDefault()
      this.dragOverIndex = null
      const from = this.draggingIndex
      this.draggingIndex = null
      if (from === null || from === index) {
        return
      }
      const moved = this.rows.splice(from, 1)[0]
      this.rows.splice(index, 0, moved)
      await this.PersistOrder()
    },
    OnDragEnd() {
      this.draggingIndex = null
      this.dragOverIndex = null
    },
    async PersistOrder() {
      const results = await Promise.allSettled(
        this.rows.map((row, index) =>
          Services.Home.UpdateHomeResource(row.uid, { sortOrder: index })
        )
      )
      const failed = results.some(
        (r) => r.status === 'rejected' || r.value?.data?.success === false
      )
      if (failed) {
        this.ShowError(null)
      }
      this.changed = true
    },
    ShowError(data) {
      const validationMessage = data?.error?.validationErrors?.[0]?.message
      const message =
        validationMessage ?? data?.error?.message ?? data?.message ?? 'Failed to save, please try again later.'
      this.$toasted.error(message).goAway(3500)
    }
  }
}
</script>

<style>
.lizard-ms-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.lizard-ms-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  border-radius: 6px;
  transition: background 0.12s ease, opacity 0.12s ease;
}
.lizard-ms-row:last-child {
  border-bottom: none;
}
.lizard-ms-row-dragging {
  opacity: 0.4;
}
.lizard-ms-row-drag-over {
  background: rgba(0, 115, 190, 0.08);
}
.lizard-ms-handle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #bbb;
  cursor: grab;
}
.lizard-ms-handle:hover {
  color: #888;
}
.lizard-ms-handle:active {
  cursor: grabbing;
}
.lizard-ms-empty {
  padding: 16px 0;
  text-align: center;
  color: #aaa;
  font-size: 13.5px;
}
.lizard-ms-input {
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 14px;
  color: #444;
  background: transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.lizard-ms-input:hover {
  background: #f8f9fa;
}
.lizard-ms-input:focus {
  outline: none;
  border-color: rgba(0, 115, 190, 0.6);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(0, 115, 190, 0.08);
}
.lizard-ms-spinner {
  flex-shrink: 0;
  color: rgba(0, 115, 190, 0.85);
}
.lizard-ms-toggle {
  flex-shrink: 0;
}
.lizard-ms-toggle:disabled {
  opacity: 0.6;
  cursor: default;
}
.lizard-ms-add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.lizard-ms-add-input {
  border: 1px solid #dfe3e8;
  border-radius: 8px;
}
.lizard-ms-add .lz-pill-btn {
  flex-shrink: 0;
  white-space: nowrap;
}
.lizard-ms-add-hint {
  margin: 6px 0 0;
}
</style>
